import type { Component } from 'solid-js'
import { For, Show } from 'solid-js'
import { Trans } from '@mbarzda/solid-i18next'
import { useChart } from '../shared/charts'
import { getDashboardDonutChartOptions, type DashboardDonutItem } from './DashboardDonutChart.vm'

interface DashboardDonutChartProps {
  titleKey: string
  items: DashboardDonutItem[]
}

const isPositive = (diff: number) => diff >= 0
const formatDiff = (diff: number) => `${Math.abs(diff * 100).toFixed(1)}%`

const DashboardDonutChart: Component<DashboardDonutChartProps> = (props) => {
  let chartRef: HTMLDivElement | undefined

  useChart({
    data: () => props.items,
    chartRef: () => chartRef,
    buildOptions: (items) => {
      const options = getDashboardDonutChartOptions(items)
      options.series = items.map((item) => item.value)
      return options
    }
  })

  return (
    <div class="flex h-full flex-col gap-4 rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
      <Show when={props.titleKey}>
        <h3 class="text-lg font-bold text-gray-900">
          <Trans key={props.titleKey} />
        </h3>
      </Show>
      <div class="flex h-full items-center gap-4">
        <div class="flex flex-[1.5] items-center justify-center">
          <div ref={chartRef} class="w-full" />
        </div>
        <div class="flex flex-1 flex-col gap-3">
          <For each={props.items}>
            {(item) => (
              <div class="flex items-center justify-between gap-4">
                <div class="flex min-w-0 items-center gap-2">
                  <div class="h-2.5 w-2.5 flex-shrink-0 rounded-full" style={{ 'background-color': item.color }} />
                  <span class="text-sm font-medium text-gray-500">{item.name}</span>
                </div>
                <div class="flex flex-shrink-0 items-center gap-3">
                  <span class="text-sm font-bold text-gray-900">{item.value.toLocaleString('en-US')}</span>
                  <div
                    class={`flex min-w-[50px] items-center justify-end text-xs font-medium ${
                      isPositive(item.diff) ? 'text-green-600' : 'text-red-600'
                    }`}
                  >
                    {isPositive(item.diff) ? '↑' : '↓'}
                    <span class="ml-0.5">{formatDiff(item.diff)}</span>
                  </div>
                </div>
              </div>
            )}
          </For>
        </div>
      </div>
    </div>
  )
}

export default DashboardDonutChart
